import React, { useState, useEffect, useRef } from 'react';
import ChatPanel from './ChatPanel';
import { Fab, CircularProgress, Grid, Box,Typography    } from '@mui/material';
import { pink } from '@mui/material/colors';
import FavoriteIcon from '@mui/icons-material/Favorite';
import FastForwardIcon from '@mui/icons-material/FastForward';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Tooltip from '@mui/material/Tooltip';
import VideocamIcon from '@mui/icons-material/Videocam'; 
import LockIcon from '@mui/icons-material/Lock';
import Confetti from 'react-confetti';
import UnlockMsg from './UnlockMsg';

const profiles = [
  {
    name: 'Windy Tavern',
    video: "windy_tavern.mp4",
    thumbnail: "windy_tavern.jpg",
    expressions: ['neutral', 'pout', 'smile', 'smirk', 'wink', 'glare'],
    expressionsFolder: "expressions",
    minIntimacy: 0
  },
  {
    name: 'Tavern Inside',
    video: "tavern_inside.mp4",
    thumbnail: "tavern_inside.jpg",
    expressions: ['neutral', 'pout', 'smile', 'frown', 'wink'],
    expressionsFolder: "expressions_inside",
    minIntimacy:1
  },
  {
    name: 'Tavern Laying',
    video: "tavern_laying.mp4",
    thumbnail: "tavern_laying.jpg",
    expressions: ['neutral', 'pout', 'smile', 'smirk', 'wink', 'glare'],
    expressionsFolder: "expressions_laying",
    minIntimacy: 3
  },
  {
    name: 'Tavern Back',
    video: "tavern_back.mp4",
    thumbnail: "tavern_back.jpg",
    expressions: ['neutral', 'pout', 'smile', 'wink','frown', 'pout'],
    expressionsFolder: "expressions_back",
    minIntimacy: 1
  },
  {
    name: 'Tavern Lay',
    video: "tavern_lay.mp4",
    thumbnail: "tavern_lay.jpg",
    expressions: ['neutral'],
    expressionsFolder: "expressions_blank",
    minIntimacy: 1
  },
  {
    name: 'Tavern Lay2',
    video: "tavern_lay2.mp4",
    thumbnail: "tavern_lay2.jpg",
    expressions: ['neutral'],
    expressionsFolder: "expressions_blank",
    minIntimacy: 2
  },
  {
    name: 'Tavern Mount',
    video: "tavern_mount.mp4",
    thumbnail: "tavern_mount.jpg",
    expressions: ['neutral'],
    expressionsFolder: "expressions_blank",
    minIntimacy: 3
  },

  {
    name: 'Sit1',
    video: "sit1.mp4",
    thumbnail: "sit1.jpg",
    expressions: ['neutral', 'smile', 'wink','frown', 'pout'],
    expressionsFolder: "expressions_sit1",
    minIntimacy: 3
  },

  {
    name: 'Sit4',
    video: "sit4.mp4",
    thumbnail: "sit4.jpg",
    expressions: ['neutral', 'smile', 'wink','frown', 'pout'],
    expressionsFolder: "expressions_sit4",
    minIntimacy: 1
  },

  {
    name: 'Sit2',
    video: "sit2.mp4",
    thumbnail: "sit2.jpg",
    expressions: ['neutral'],
    expressionsFolder: "expressions_blank",
    minIntimacy: 2
  },

  {
    name: 'Sit3',
    video: "sit3.mp4",
    thumbnail: "sit3.jpg",
    expressions: ['neutral'],
    expressionsFolder: "expressions_blank",
    minIntimacy: 3
  },

  {
    name: 'Sit5',
    video: "sit5.mp4",
    thumbnail: "sit5.jpg",
    expressions: ['neutral'],
    expressionsFolder: "expressions_blank",
    minIntimacy: 2
  },
];

const APP_ID = "MidnightMirth";

function App() {
  const [expression, setExpression] = useState('neutral');
  const [profileIdx, setProfileIdx] = useState(() => {
    const saved = localStorage.getItem(APP_ID+'_profileIdx');
    return saved !== null ? parseInt(saved, 10) : 0;
  });
  const [intimacy, setIntimacy] = useState(() => {
    const saved = localStorage.getItem(APP_ID+'_intimacy');
    return saved !== null ? parseInt(saved, 10) : 0;
  });
  const [progress, setProgress] = useState(() => {
    const saved = localStorage.getItem(APP_ID+'_progress');
    return saved !== null ? parseFloat(saved) : 0;
  });
  const [showChatPanel, setShowChatPanel] = useState(true);
  const [showProfileSelector, setShowProfileSelector] = useState(false);
  const [showUnlockMessage, setShowUnlockMessage] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);

  const maxIntimacy = 3;
  const progressThreshold = 100;

  const sceneButtonRef = useRef(null);

  useEffect(() => {
    if (progress >= progressThreshold) {
      increaseIntimacy();
    }
  }, [progress]);

  useEffect(() => {
    localStorage.setItem(APP_ID+'_intimacy', intimacy.toString());
    localStorage.setItem(APP_ID+'_progress', progress.toString());
  }, [intimacy, progress]);

  const increaseIntimacy = () => {
    if (intimacy < maxIntimacy) {
      setIntimacy(prevIntimacy => {
        const newIntimacy = Math.min(prevIntimacy + 1, maxIntimacy);
        if (newIntimacy > prevIntimacy) {
          setShowUnlockMessage(true);
          setShowConfetti(true);
          setTimeout(() => setShowConfetti(false), 3000);
        }
        return newIntimacy;
      });
      setProgress(0);
    }
  };

  const handleHeartClick = () => {
    // This function now only triggers confetti
    //setShowConfetti(true);
    //setTimeout(() => setShowConfetti(false), 3000); // Stop confetti after 3 seconds
    //setProgress(p=>p+20);
  };
  ///
  
  const videoRef = useRef(null);
  const profileSelectorRef = useRef(null);

  const toggleChatPanel = () => {
    setShowChatPanel(!showChatPanel);
  };

  const handleVideocamClick = () => {
    setShowProfileSelector(!showProfileSelector);
  };

  const handleClickOutside = (event) => {
    if (profileSelectorRef.current && !profileSelectorRef.current.contains(event.target)) {
      setShowProfileSelector(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    document.title = 'Midnight Mirth at the Tavern';
    
    const changeExpression = () => {
      setExpression('closed');
      setTimeout(() => {
        setExpression(prev => prev === 'closed' ? 'neutral' : prev);
      }, 1000);
    };

    const timer = setTimeout(changeExpression, Math.random() * 8000 + 8000);

    return () => clearTimeout(timer);
  }, [expression]);

  

  useEffect(() => {
    localStorage.setItem(APP_ID+'_profileIdx', profileIdx.toString());
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, [profileIdx]);

  const handleButtonClick = () => {
    const maxProgress = 100;

    if (progress < maxProgress) {
      const newProgress = Math.min(progress + 10, maxProgress);
      setProgress(newProgress);
      const newIntimacy = Math.min(Math.floor(newProgress / 25), maxIntimacy);
      if (newIntimacy > intimacy) {
        setShowUnlockMessage(true);
      }
      setIntimacy(newIntimacy);
    }
  };

  const resetScene = () => {
    setProgress(0);
    setIntimacy(0);
    setProfileIdx(0);
    localStorage.removeItem(APP_ID+'_progress');
    localStorage.removeItem(APP_ID+'_intimacy');
    localStorage.removeItem(APP_ID+'_profileIdx');
  };

  const handleProfileSelect = (index) => {
    if (intimacy >= profiles[index].minIntimacy) {
      setProfileIdx(index);
      setShowProfileSelector(false);
    } else {
      //alert(`You need at least ${profiles[index].minIntimacy} intimacy to select this profile.`);
    }
  };

  const currentProfile = profiles[profileIdx];
  const { video, expressionsFolder, expressions, thumbnail } = currentProfile;

  const maxProgress = 100;
  const normalizedProgress = (progress / maxProgress) * 100;

  const buttonSx = {
    ...(progress >= maxProgress && {
      bgcolor: 'primary.main',
      '&:hover': {
        bgcolor: 'primary.dark',
      },
    }),
    ...((progress >= 0 && progress < maxProgress) && {
      bgcolor: pink[500],
      '&:hover': {
        bgcolor: pink[700],
      },
    }),
    transition: 'all 0.3s ease-in-out',
    transform: progress >= maxProgress ? 'scale(1.1)' : 'scale(1)',
  };

  const handleCloseUnlockMessage = () => {
    setShowUnlockMessage(false);
  };
  return (
    <div className="app">
      {showConfetti && <Confetti />}
      <video ref={videoRef} key={video} autoPlay loop muted>
        <source src={'./videos/'+video} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
      <img src={`${expressionsFolder}/neutral.png`} alt="Overlay" className="overlay-image" />
      <img src={`${expressionsFolder}/${expression}.png`} alt="Overlay" className="overlay-image" />

      <div style={{
        display: showChatPanel ? 'block' : 'none'
      }}>
        <ChatPanel 
          setExpressionImage={setExpression} 
          expressions={expressions} 
          setProgress={setProgress} 
          resetScene={resetScene}
          setProfileIdx={()=>{}} //DISABLED
        />
      </div>

      <div style={{
        position: 'absolute',
        top: '20px',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        gap: '10px',
      }}>
        {showChatPanel && <Tooltip title='Scene Selection' placement='bottom'>
          <Fab
            ref={sceneButtonRef}
            aria-label="scene selection"
            color="default"
            size='small'
            onClick={handleVideocamClick}
          >
            <VideocamIcon color='primary' />
          </Fab>
        </Tooltip>}
        {showChatPanel && <Tooltip title={'Intimacy Level: '+intimacy} placement='bottom'>
          <div style={{ position: 'relative' }}>
            <Fab
              aria-label="intimacy level"
              color="secondary"
              sx={{
                bgcolor: pink[500],
                '&:hover': {
                  bgcolor: pink[700],
                },
              }}
              onClick={handleHeartClick}
            >
              <FavoriteIcon />
            </Fab>
            {intimacy < maxIntimacy && (
              <CircularProgress
                variant="determinate"
                value={progress}
                size={68}
                sx={{
                  color: 'primary.main',
                  position: 'absolute',
                  top: -6,
                  left: -6,
                  zIndex: 1,
                }}
              />
            )}
            {intimacy === maxIntimacy && (
              <Typography
                variant="caption"
                sx={{
                  position: 'absolute',
                  bottom: -24,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  color: pink[500],
                  fontWeight: 'bold',
                  bgcolor: 'rgba(255, 255, 255, 1)',
                  padding: '2px 8px',
                  borderRadius: '12px',
                  whiteSpace: 'nowrap',
                }}
              >
                Maxed!
              </Typography>
            )}
          </div>
        </Tooltip>}
        <Tooltip title='Hide Chat' placement='bottom'>
          <Fab
            aria-label="toggle chat panel"
            color="default"
            size='small'
            onClick={toggleChatPanel}
          >
            {!showChatPanel ? <VisibilityOffIcon color='info' /> : <VisibilityIcon color='primary' />}
          </Fab>
        </Tooltip>
      </div>
      {showProfileSelector && (
        <Box
        ref={profileSelectorRef}
        sx={{
          position: 'absolute',
          top: '80px',
          left: '50%',
          transform: 'translateX(-50%)',
          bgcolor: 'rgba(0, 0, 139, 0.5)', // Updated to a dark blue with 50% opacity
          background: 'linear-gradient(to bottom, rgba(68, 118, 169, 0.75), rgba(53, 90, 128, 0.75))', // Gradient from blue to dark blue
          boxShadow: 24,
          p: 2,
          borderRadius: 2,
          maxWidth: '90%',
          width: '100%',
          maxHeight: '80vh',
          overflowY: 'auto',
        }}
      >
      
      <Grid container spacing={1} justifyContent="center">
  {profiles
    .map((profile, index) => ({ profile, originalIndex: index })) // Preserve original index
    .sort((a, b) => a.profile.minIntimacy - b.profile.minIntimacy) // Sort based on minIntimacy
    .map(({ profile, originalIndex }, sortedIndex) => (
      <Grid item xs={6} sm={4} md={3} lg={2} key={sortedIndex}>
        <Box
          sx={{
            cursor: 'pointer',
            '&:hover': {
              opacity: 0.8,
            },
            width: '100%',
            paddingTop: '56.25%', // 16:9 Aspect Ratio
            position: 'relative',
          }}
          onClick={() => handleProfileSelect(originalIndex)} // Use original index
        >
          <img 
            src={'./thumbnails/' + profile.thumbnail} 
            alt={profile.name}
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              filter: intimacy >= profile.minIntimacy ? 'none' : 'brightness(50%)',
            }}
          />
          {intimacy < profile.minIntimacy && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                color: 'white',
              }}
            >
              <LockIcon sx={{ fontSize: 40 }} />
              <Box sx={{ mt: 1, fontSize: '0.8rem', textAlign: 'center' }}>
                Lv {profile.minIntimacy}
              </Box>
            </Box>
          )}
        </Box>
      </Grid>
    ))}
</Grid>

        </Box>
      )}
     <UnlockMsg
  anchorEl={sceneButtonRef.current}
  open={showUnlockMessage}
  onClose={handleCloseUnlockMessage}
/>
    </div>
  );
}

export default App;